import { m } from "@plannello/i18n/messages";
import { adminError } from "@/components/admin/errors";
import { ApiError } from "@/lib/api";

const names = (v: unknown): string =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").join(", ") : "";

/**
 * Turns the schema admin API's error codes (409 domain conflicts and 422 validation reasons) into a
 * sentence that says what is wrong and what to do about it. Unknown failures fall back to the
 * generic admin copy.
 */
export function schemaError(e: unknown): string {
  if (!(e instanceof ApiError)) return m.error_generic();
  const d = e.details ?? {};
  const count = typeof d.count === "number" ? d.count : 0;
  switch (e.code) {
    case "hierarchy_conflict":
      return count === 1 ? m.sch_err_hierarchy_one() : m.sch_err_hierarchy({ count });
    case "default_type_protected":
      return m.sch_err_default_type();
    case "builtin_type":
      return m.sch_err_builtin_type();
    case "type_in_use":
      return count === 1 ? m.sch_err_type_in_use_one() : m.sch_err_type_in_use({ count });
    case "type_referenced": {
      const list = [names(d.types), names(d.groups), names(d.fields)].filter(Boolean).join(", ");
      return m.sch_err_type_referenced({ names: list });
    }
    case "builtin_workflow":
      return m.sch_err_builtin_workflow();
    case "workflow_in_use":
      return count === 1 ? m.sch_err_workflow_in_use_one() : m.sch_err_workflow_in_use({ count });
    case "workflow_has_history":
      return m.sch_err_workflow_history();
    case "status_in_use":
      return m.sch_err_status_in_use();
    case "validation":
      break;
    default:
      return adminError(e);
  }
  switch (e.reason) {
    case "slug_taken":
      return m.sch_err_slug_taken();
    case "parent_type_level":
      return m.sch_err_parent_level();
    case "parent_type_self":
      return m.sch_err_parent_self();
    case "type_not_found":
      return m.sch_err_type_missing();
    case "type_archived":
      return m.sch_err_type_archived();
    case "same_type":
      return m.sch_err_same_type();
    case "workflow_invariant":
      return invariantMessage(d.missing);
    case "too_many_statuses":
      return m.sch_err_too_many_statuses({ max: typeof d.max === "number" ? d.max : 30 });
    case "remap_required":
      return m.sch_err_remap_required();
    case "same_status":
    case "status_not_in_workflow":
    case "status_archived":
      return m.sch_err_status_target();
    case "workflow_archived":
      return m.sch_err_workflow_archived();
    case "status_mapping_incomplete":
      return m.sch_err_mapping_incomplete();
    case "status_mapping_invalid":
      return m.sch_err_mapping_invalid();
    default:
      return m.error_generic();
  }
}

/** The workflow rule in plain words: what it still lacks. */
export function invariantMessage(missing: unknown): string {
  const list = Array.isArray(missing) ? missing : [];
  const open = list.includes("backlog_or_unstarted");
  const done = list.includes("completed");
  if (open && done) return m.sch_err_invariant_both();
  if (open) return m.sch_err_invariant_open();
  if (done) return m.sch_err_invariant_completed();
  return m.sch_err_invariant_both();
}
